// Geometry for the hand-rolled SVG history chart: the line and area paths,
// "nice" value ticks, date ticks, and the nearest point to a pointer. Pure
// functions of the points and the pixel box, so the component only draws.

import type { Point } from "./stablecoins-view";

const DAY_MS = 86_400_000;

export interface ChartBox {
  width: number;
  height: number;
  /** Room for the value labels on the left. */
  padLeft: number;
  padRight: number;
  padTop: number;
  /** Room for the date labels at the bottom. */
  padBottom: number;
}

export const DEFAULT_BOX: ChartBox = { width: 640, height: 220, padLeft: 56, padRight: 12, padTop: 10, padBottom: 24 };

export interface Tick {
  value: number;
  /** Pixel position along the axis. */
  at: number;
  label: string;
}

export interface ChartGeometry {
  /** SVG path of the line, "M x,y L x,y …". */
  path: string;
  /** The line closed down to the baseline, for the fill. */
  area: string;
  xTicks: Tick[];
  yTicks: Tick[];
  min: number;
  max: number;
  /** Pixel coordinates of every point, in order. */
  pixels: Array<{ x: number; y: number }>;
  x: (t: number) => number;
  y: (value: number) => number;
}

/** "$180B", "$1.2T", "$950M": short enough for an axis. */
export function axisUsd(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const short = (n: number, unit: string) => `${sign}$${n >= 100 ? n.toFixed(0) : n >= 10 ? n.toFixed(1).replace(/\.0$/, "") : n.toFixed(2).replace(/\.?0+$/, "")}${unit}`;
  if (abs >= 1e12) return short(abs / 1e12, "T");
  if (abs >= 1e9) return short(abs / 1e9, "B");
  if (abs >= 1e6) return short(abs / 1e6, "M");
  if (abs >= 1e3) return short(abs / 1e3, "K");
  return `${sign}$${abs.toFixed(0)}`;
}

/** "Oct 5" within a span of half a year, "Oct 2025" beyond (so a year's ends never read alike); always UTC. */
export function dateLabel(ms: number, spanMs: number): string {
  const d = new Date(ms);
  const options: Intl.DateTimeFormatOptions =
    spanMs > 180 * DAY_MS ? { month: "short", year: "numeric", timeZone: "UTC" } : { month: "short", day: "numeric", timeZone: "UTC" };
  return d.toLocaleDateString("en-US", options);
}

/** "2026-10-05": the full day for a readout. */
export function dayText(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * About `count` evenly spaced round values covering [min, max]. The step is
 * 1, 2 or 5 times a power of ten (d3's cutoffs, √2, √10 and √50, keep the
 * tick count near the request), so labels read as $170B, $175B, $180B.
 */
export function niceTicks(min: number, max: number, count = 4): number[] {
  if (!(count > 0) || !Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (max <= min) return [min];
  const rough = (max - min) / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  const step = (residual >= Math.sqrt(50) ? 10 : residual >= Math.sqrt(10) ? 5 : residual >= Math.sqrt(2) ? 2 : 1) * magnitude;
  const ticks: number[] = [];
  for (let i = Math.ceil(min / step - 1e-9); i * step <= max + step * 1e-9; i += 1) ticks.push(Number((i * step).toPrecision(12)));
  return ticks;
}

/** Evenly spaced date ticks, first and last points included. */
export function timeTicks(t0: number, t1: number, count = 4): number[] {
  if (count < 2 || t1 <= t0) return [t0];
  return Array.from({ length: count }, (_, i) => t0 + ((t1 - t0) * i) / (count - 1));
}

/** Where the points land in the box; needs at least two points to draw a line. */
/** "3.90%": a rate (fraction) for an axis. */
export const axisRate = (value: number): string => `${(value * 100).toFixed(2)}%`;

export function chartGeometry(
  points: readonly Point[],
  box: ChartBox = DEFAULT_BOX,
  valueTicks = 4,
  axisLabel: (value: number) => string = axisUsd,
): ChartGeometry | null {
  if (points.length < 2) return null;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const t0 = first[0];
  const t1 = last[0];
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const [, v] of points) {
    if (v < min) min = v;
    if (v > max) max = v;
  }
  // A flat line still gets a band to sit in, and the axis pads the extremes a little.
  const span = max - min || Math.abs(max) * 0.1 || 1;
  const lo = min - span * 0.08;
  const hi = max + span * 0.08;
  const innerW = Math.max(1, box.width - box.padLeft - box.padRight);
  const innerH = Math.max(1, box.height - box.padTop - box.padBottom);
  const spanT = Math.max(1, t1 - t0);
  const x = (t: number) => box.padLeft + ((t - t0) / spanT) * innerW;
  const y = (v: number) => box.padTop + innerH - ((v - lo) / (hi - lo)) * innerH;
  const pixels = points.map(([t, v]) => ({ x: x(t), y: y(v) }));
  const path = pixels.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const baseline = (box.padTop + innerH).toFixed(1);
  const area = `${path} L${pixels[pixels.length - 1]!.x.toFixed(1)},${baseline} L${pixels[0]!.x.toFixed(1)},${baseline} Z`;
  const yTicks = niceTicks(lo, hi, valueTicks)
    .filter((v) => v >= lo && v <= hi)
    .map((v) => ({ value: v, at: y(v), label: axisLabel(v) }));
  // A span of a few days can put two ticks on one date; each label is drawn once.
  const xTicks = timeTicks(t0, t1, box.width < 420 ? 3 : 4)
    .map((t) => ({ value: t, at: x(t), label: dateLabel(t, t1 - t0) }))
    .filter((tick, index, all) => all.findIndex((other) => other.label === tick.label) === index);
  return { path, area, xTicks, yTicks, min, max, pixels, x, y };
}

/** Index of the point nearest in time to `t`. */
export function nearestIndex(points: readonly Point[], t: number): number {
  if (points.length === 0) return -1;
  let lo = 0;
  let hi = points.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid]![0] < t) lo = mid + 1;
    else hi = mid;
  }
  if (lo > 0 && Math.abs(points[lo - 1]![0] - t) <= Math.abs(points[lo]![0] - t)) return lo - 1;
  return lo;
}
